import React, { useState, useCallback } from 'react';
import { View, StyleSheet, FlatList, Alert } from 'react-native';
import {
  Text,
  Card,
  Button,
  IconButton,
  useTheme,
  Avatar,
  TextInput,
  Chip,
  Modal,
  Portal,
} from 'react-native-paper';
import {
  MessageCircle,
  Clock,
  Check,
  X,
  Users,
  Sparkles,
  Send,
  User,
} from 'lucide-react-native';
import type {
  ConnectionRequest,
  PeerProfile,
} from '../../store/slices/peerMatchingSlice';

interface ConnectionRequestSystemProps {
  sentRequests: ConnectionRequest[];
  receivedRequests: ConnectionRequest[];
  onRespondToRequest: (
    requestId: string,
    action: 'accept' | 'decline',
    message?: string
  ) => void;
  onCancelRequest: (requestId: string) => void;
  onSendRequest: (
    toUserId: string,
    message?: string,
    useAIIntro?: boolean
  ) => void;
  loading?: boolean;
}

interface AIIntroModalProps {
  visible: boolean;
  peer: PeerProfile;
  onDismiss: () => void;
  onSend: (message: string, useAI: boolean) => void;
}

function AIIntroModal({ visible, peer, onDismiss, onSend }: AIIntroModalProps) {
  const theme = useTheme();
  const [customMessage, setCustomMessage] = useState('');
  const [useAIIntro, setUseAIIntro] = useState(true);
  const [aiGeneratedMessage, setAiGeneratedMessage] = useState('');

  // Generate AI introduction message
  const generateAIIntro = useCallback(() => {
    // This would typically call an AI service
    const intros = [
      `Hi ${peer.displayName}! I noticed we share similar interests in ${peer.masteryAreas[0]?.replace(/_/g, ' ')} and have compatible learning goals. I'd love to connect and potentially study together!`,
      `Hello! I'm impressed by your expertise in ${peer.masteryAreas[0]?.replace(/_/g, ' ')}. Our skill levels seem well-matched, and I think we could learn a lot from each other. Would you be interested in connecting?`,
      `Hi there! I see we're both working on similar coffee skills and have overlapping availability. I'd enjoy connecting to share experiences and maybe collaborate on some learning activities!`,
    ];

    const randomIntro = intros[Math.floor(Math.random() * intros.length)];
    setAiGeneratedMessage(randomIntro);
  }, [peer]);

  React.useEffect(() => {
    if (visible && useAIIntro) {
      generateAIIntro();
    }
  }, [visible, useAIIntro, generateAIIntro]);

  const handleSend = () => {
    const message = useAIIntro ? aiGeneratedMessage : customMessage;
    onSend(message, useAIIntro);
    onDismiss();
    setCustomMessage('');
    setAiGeneratedMessage('');
  };

  return (
    <Portal>
      <Modal
        visible={visible}
        onDismiss={onDismiss}
        contentContainerStyle={[
          styles.modalContainer,
          { backgroundColor: theme.colors.surface },
        ]}
      >
        <View style={styles.modalHeader}>
          <Text
            variant='titleMedium'
            style={[styles.modalTitle, { color: theme.colors.onSurface }]}
          >
            Send Connection Request
          </Text>
          <IconButton icon={X} size={20} onPress={onDismiss} />
        </View>

        <View style={styles.peerInfo}>
          <Avatar.Image
            size={40}
            source={{ uri: peer.avatarUrl || 'https://via.placeholder.com/40' }}
          />
          <View style={styles.peerDetails}>
            <Text
              variant='bodyMedium'
              style={[styles.peerName, { color: theme.colors.onSurface }]}
            >
              {peer.displayName}
            </Text>
            <Text
              variant='bodySmall'
              style={{ color: theme.colors.onSurfaceVariant }}
            >
              @{peer.username}
            </Text>
          </View>
        </View>

        <View style={styles.messageOptions}>
          <View style={styles.optionHeader}>
            <Button
              mode={useAIIntro ? 'contained' : 'outlined'}
              onPress={() => setUseAIIntro(true)}
              style={styles.optionButton}
              labelStyle={styles.optionButtonLabel}
              icon={Sparkles}
              compact
            >
              AI Generated
            </Button>
            <Button
              mode={!useAIIntro ? 'contained' : 'outlined'}
              onPress={() => setUseAIIntro(false)}
              style={styles.optionButton}
              labelStyle={styles.optionButtonLabel}
              icon={MessageCircle}
              compact
            >
              Custom Message
            </Button>
          </View>

          {useAIIntro ? (
            <View style={styles.aiMessageContainer}>
              <View style={styles.aiMessageHeader}>
                <Sparkles size={16} color={theme.colors.primary} />
                <Text
                  variant='bodySmall'
                  style={[styles.aiLabel, { color: theme.colors.primary }]}
                >
                  AI-Generated Introduction
                </Text>
                <Button
                  mode='text'
                  onPress={generateAIIntro}
                  labelStyle={styles.regenerateLabel}
                  compact
                >
                  Regenerate
                </Button>
              </View>
              <Text
                variant='bodyMedium'
                style={[styles.aiMessage, { color: theme.colors.onSurface }]}
              >
                {aiGeneratedMessage}
              </Text>
            </View>
          ) : (
            <TextInput
              mode='outlined'
              label='Your message'
              value={customMessage}
              onChangeText={setCustomMessage}
              multiline
              numberOfLines={4}
              placeholder='Write a personal introduction message...'
              style={styles.customMessageInput}
            />
          )}
        </View>

        <View style={styles.modalActions}>
          <Button
            mode='outlined'
            onPress={onDismiss}
            style={styles.modalActionButton}
          >
            Cancel
          </Button>
          <Button
            mode='contained'
            onPress={handleSend}
            style={[styles.modalActionButton, styles.sendButton]}
            labelStyle={styles.sendButtonLabel}
            icon={Send}
            disabled={useAIIntro ? !aiGeneratedMessage : !customMessage.trim()}
          >
            Send Request
          </Button>
        </View>
      </Modal>
    </Portal>
  );
}

export function ConnectionRequestSystem({
  sentRequests,
  receivedRequests,
  onRespondToRequest,
  onCancelRequest,
  onSendRequest,
  loading = false,
}: ConnectionRequestSystemProps) {
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState<'received' | 'sent'>('received');
  const [responseMessage, setResponseMessage] = useState('');
  const [respondingToId, setRespondingToId] = useState<string | null>(null);

  const handleAcceptRequest = useCallback(
    (requestId: string) => {
      onRespondToRequest(requestId, 'accept', responseMessage || undefined);
      setResponseMessage('');
      setRespondingToId(null);
    },
    [onRespondToRequest, responseMessage]
  );

  const handleDeclineRequest = useCallback(
    (requestId: string) => {
      Alert.alert(
        'Decline Connection Request',
        'Are you sure you want to decline this connection request?',
        [
          { text: 'Cancel', style: 'cancel' },
          {
            text: 'Decline',
            style: 'destructive',
            onPress: () => {
              onRespondToRequest(requestId, 'decline');
              setRespondingToId(null);
            },
          },
        ]
      );
    },
    [onRespondToRequest]
  );

  const handleCancelRequest = useCallback(
    (requestId: string) => {
      Alert.alert(
        'Cancel Connection Request',
        'Are you sure you want to cancel this connection request?',
        [
          { text: 'No', style: 'cancel' },
          {
            text: 'Yes, Cancel',
            style: 'destructive',
            onPress: () => onCancelRequest(requestId),
          },
        ]
      );
    },
    [onCancelRequest]
  );

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor(
      (now.getTime() - date.getTime()) / (1000 * 60 * 60)
    );

    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours}h ago`;
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays}d ago`;
    return date.toLocaleDateString();
  };

  const renderReceivedRequest = ({ item }: { item: ConnectionRequest }) => (
    <Card
      style={[styles.requestCard, { backgroundColor: theme.colors.surface }]}
    >
      <Card.Content>
        <View style={styles.requestHeader}>
          <View style={styles.requestInfo}>
            <Avatar.Image
              size={40}
              source={{ uri: 'https://via.placeholder.com/40' }} // TODO: Get from user data
            />
            <View style={styles.requestDetails}>
              <Text
                variant='bodyMedium'
                style={[
                  styles.requesterName,
                  { color: theme.colors.onSurface },
                ]}
              >
                Connection Request
              </Text>
              <Text
                variant='bodySmall'
                style={{ color: theme.colors.onSurfaceVariant }}
              >
                {formatTimeAgo(item.createdAt)}
              </Text>
            </View>
          </View>

          <View style={styles.requestStatus}>
            {item.aiGeneratedIntro && (
              <Chip
                mode='outlined'
                textStyle={styles.aiChipText}
                style={[styles.aiChip, { borderColor: theme.colors.primary }]}
                icon={() => <Sparkles size={10} color={theme.colors.primary} />}
              >
                AI Intro
              </Chip>
            )}
          </View>
        </View>

        <Text
          variant='bodyMedium'
          style={[styles.requestMessage, { color: theme.colors.onSurface }]}
        >
          {item.message}
        </Text>

        {item.status === 'pending' && (
          <View style={styles.requestActions}>
            {respondingToId === item.id ? (
              <View style={styles.responseContainer}>
                <TextInput
                  mode='outlined'
                  label='Optional response message'
                  value={responseMessage}
                  onChangeText={setResponseMessage}
                  placeholder='Add a personal message (optional)'
                  style={styles.responseInput}
                  dense
                />
                <View style={styles.responseButtons}>
                  <Button
                    mode='outlined'
                    onPress={() => setRespondingToId(null)}
                    style={styles.responseButton}
                    compact
                  >
                    Cancel
                  </Button>
                  <Button
                    mode='contained'
                    onPress={() => handleAcceptRequest(item.id)}
                    style={[styles.responseButton, styles.acceptButton]}
                    labelStyle={styles.acceptButtonLabel}
                    icon={Check}
                    compact
                  >
                    Accept
                  </Button>
                </View>
              </View>
            ) : (
              <View style={styles.actionButtons}>
                <Button
                  mode='outlined'
                  onPress={() => handleDeclineRequest(item.id)}
                  style={styles.actionButton}
                  labelStyle={styles.declineButtonLabel}
                  icon={X}
                  compact
                >
                  Decline
                </Button>
                <Button
                  mode='contained'
                  onPress={() => setRespondingToId(item.id)}
                  style={[styles.actionButton, styles.acceptButton]}
                  labelStyle={styles.acceptButtonLabel}
                  icon={Check}
                  compact
                >
                  Accept
                </Button>
              </View>
            )}
          </View>
        )}

        {item.status === 'accepted' && (
          <View style={styles.statusContainer}>
            <Check size={16} color='#4ECDC4' />
            <Text
              variant='bodySmall'
              style={[styles.statusText, { color: '#4ECDC4' }]}
            >
              Connection accepted
            </Text>
          </View>
        )}

        {item.status === 'declined' && (
          <View style={styles.statusContainer}>
            <X size={16} color='#E74C3C' />
            <Text
              variant='bodySmall'
              style={[styles.statusText, { color: '#E74C3C' }]}
            >
              Connection declined
            </Text>
          </View>
        )}
      </Card.Content>
    </Card>
  );

  const renderSentRequest = ({ item }: { item: ConnectionRequest }) => (
    <Card
      style={[styles.requestCard, { backgroundColor: theme.colors.surface }]}
    >
      <Card.Content>
        <View style={styles.requestHeader}>
          <View style={styles.requestInfo}>
            <Avatar.Image
              size={40}
              source={{ uri: 'https://via.placeholder.com/40' }} // TODO: Get from user data
            />
            <View style={styles.requestDetails}>
              <Text
                variant='bodyMedium'
                style={[
                  styles.requesterName,
                  { color: theme.colors.onSurface },
                ]}
              >
                Request Sent
              </Text>
              <Text
                variant='bodySmall'
                style={{ color: theme.colors.onSurfaceVariant }}
              >
                {formatTimeAgo(item.createdAt)}
              </Text>
            </View>
          </View>

          <View style={styles.requestStatus}>
            {item.status === 'pending' && (
              <Chip
                mode='outlined'
                textStyle={[styles.statusChipText, { color: '#FF6B35' }]}
                style={[styles.statusChip, { borderColor: '#FF6B35' }]}
                icon={() => <Clock size={10} color='#FF6B35' />}
              >
                Pending
              </Chip>
            )}
            {item.status === 'accepted' && (
              <Chip
                mode='filled'
                textStyle={[styles.statusChipText, { color: 'white' }]}
                style={[styles.statusChip, { backgroundColor: '#4ECDC4' }]}
                icon={() => <Check size={10} color='white' />}
              >
                Accepted
              </Chip>
            )}
            {item.status === 'declined' && (
              <Chip
                mode='filled'
                textStyle={[styles.statusChipText, { color: 'white' }]}
                style={[styles.statusChip, { backgroundColor: '#E74C3C' }]}
                icon={() => <X size={10} color='white' />}
              >
                Declined
              </Chip>
            )}
          </View>
        </View>

        <Text
          variant='bodyMedium'
          style={[styles.requestMessage, { color: theme.colors.onSurface }]}
        >
          {item.message}
        </Text>

        {item.status === 'pending' && (
          <View style={styles.sentRequestActions}>
            <Button
              mode='outlined'
              onPress={() => handleCancelRequest(item.id)}
              style={styles.cancelButton}
              labelStyle={styles.cancelButtonLabel}
              icon={X}
              compact
            >
              Cancel Request
            </Button>
          </View>
        )}

        {item.status === 'accepted' && (
          <View style={styles.sentRequestActions}>
            <Button
              mode='contained'
              onPress={() => {
                /* Navigate to chat */
              }}
              style={styles.chatButton}
              labelStyle={styles.chatButtonLabel}
              icon={MessageCircle}
              compact
            >
              Start Conversation
            </Button>
          </View>
        )}
      </Card.Content>
    </Card>
  );

  return (
    <View style={styles.container}>
      {/* Tab Navigation */}
      <View style={styles.tabContainer}>
        <Button
          mode={activeTab === 'received' ? 'contained' : 'outlined'}
          onPress={() => setActiveTab('received')}
          style={styles.tabButton}
          labelStyle={styles.tabButtonLabel}
          icon={Users}
          compact
        >
          Received (
          {receivedRequests.filter(r => r.status === 'pending').length})
        </Button>
        <Button
          mode={activeTab === 'sent' ? 'contained' : 'outlined'}
          onPress={() => setActiveTab('sent')}
          style={styles.tabButton}
          labelStyle={styles.tabButtonLabel}
          icon={Send}
          compact
        >
          Sent ({sentRequests.length})
        </Button>
      </View>

      {/* Request Lists */}
      {activeTab === 'received' ? (
        <FlatList
          data={receivedRequests}
          renderItem={renderReceivedRequest}
          keyExtractor={item => item.id}
          contentContainerStyle={styles.listContainer}
          showsVerticalScrollIndicator={false}
          ListEmptyComponent={
            <View style={styles.emptyContainer}>
              <Users size={48} color={theme.colors.onSurfaceVariant} />
              <Text
                variant='bodyLarge'
                style={[
                  styles.emptyText,
                  { color: theme.colors.onSurfaceVariant },
                ]}
              >
                No connection requests
              </Text>
              <Text
                variant='bodySmall'
                style={[
                  styles.emptySubtext,
                  { color: theme.colors.onSurfaceVariant },
                ]}
              >
                When someone wants to connect with you, their requests will
                appear here
              </Text>
            </View>
          }
        />
      ) : (
        <FlatList
          data={sentRequests}
          renderItem={renderSentRequest}
          keyExtractor={item => item.id}
          contentContainerStyle={styles.listContainer}
          showsVerticalScrollIndicator={false}
          ListEmptyComponent={
            <View style={styles.emptyContainer}>
              <Send size={48} color={theme.colors.onSurfaceVariant} />
              <Text
                variant='bodyLarge'
                style={[
                  styles.emptyText,
                  { color: theme.colors.onSurfaceVariant },
                ]}
              >
                No sent requests
              </Text>
              <Text
                variant='bodySmall'
                style={[
                  styles.emptySubtext,
                  { color: theme.colors.onSurfaceVariant },
                ]}
              >
                Connection requests you send will be tracked here
              </Text>
            </View>
          }
        />
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  tabContainer: {
    flexDirection: 'row',
    padding: 16,
    gap: 12,
  },
  tabButton: {
    flex: 1,
  },
  tabButtonLabel: {
    fontSize: 12,
  },
  listContainer: {
    padding: 16,
    paddingTop: 0,
  },
  requestCard: {
    marginBottom: 12,
    elevation: 1,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
  },
  requestHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 12,
  },
  requestInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  requestDetails: {
    marginLeft: 12,
    flex: 1,
  },
  requesterName: {
    fontWeight: '600',
    marginBottom: 2,
  },
  requestStatus: {
    alignItems: 'flex-end',
  },
  aiChip: {
    height: 20,
  },
  aiChipText: {
    fontSize: 9,
    fontWeight: '600',
  },
  statusChip: {
    height: 20,
  },
  statusChipText: {
    fontSize: 9,
    fontWeight: '600',
  },
  requestMessage: {
    lineHeight: 20,
    marginBottom: 16,
    fontStyle: 'italic',
  },
  requestActions: {
    gap: 12,
  },
  actionButtons: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 12,
  },
  actionButton: {
    flex: 1,
  },
  acceptButton: {
    backgroundColor: '#4ECDC4',
  },
  acceptButtonLabel: {
    color: 'white',
    fontSize: 12,
  },
  declineButtonLabel: {
    fontSize: 12,
  },
  responseContainer: {
    gap: 12,
  },
  responseInput: {
    fontSize: 14,
  },
  responseButtons: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 12,
  },
  responseButton: {
    flex: 1,
  },
  statusContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    justifyContent: 'center',
    paddingVertical: 8,
  },
  statusText: {
    fontWeight: '600',
    fontSize: 12,
  },
  sentRequestActions: {
    alignItems: 'center',
  },
  cancelButton: {
    minWidth: 140,
  },
  cancelButtonLabel: {
    fontSize: 12,
  },
  chatButton: {
    backgroundColor: '#4ECDC4',
    minWidth: 160,
  },
  chatButtonLabel: {
    color: 'white',
    fontSize: 12,
  },
  emptyContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 48,
    paddingHorizontal: 32,
  },
  emptyText: {
    fontWeight: '600',
    marginTop: 16,
    marginBottom: 8,
    textAlign: 'center',
  },
  emptySubtext: {
    textAlign: 'center',
    lineHeight: 18,
  },
  modalContainer: {
    margin: 20,
    borderRadius: 12,
    padding: 20,
    maxHeight: '80%',
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  modalTitle: {
    fontWeight: '600',
  },
  peerInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 20,
    paddingBottom: 16,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(0,0,0,0.1)',
  },
  peerDetails: {
    marginLeft: 12,
  },
  peerName: {
    fontWeight: '600',
    marginBottom: 2,
  },
  messageOptions: {
    marginBottom: 20,
  },
  optionHeader: {
    flexDirection: 'row',
    gap: 12,
    marginBottom: 16,
  },
  optionButton: {
    flex: 1,
  },
  optionButtonLabel: {
    fontSize: 12,
  },
  aiMessageContainer: {
    backgroundColor: 'rgba(0,0,0,0.02)',
    borderRadius: 8,
    padding: 12,
  },
  aiMessageHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginBottom: 8,
  },
  aiLabel: {
    fontSize: 12,
    fontWeight: '600',
    flex: 1,
  },
  regenerateLabel: {
    fontSize: 11,
  },
  aiMessage: {
    lineHeight: 18,
  },
  customMessageInput: {
    fontSize: 14,
  },
  modalActions: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 12,
  },
  modalActionButton: {
    flex: 1,
  },
  sendButton: {
    backgroundColor: '#4ECDC4',
  },
  sendButtonLabel: {
    color: 'white',
  },
});
